import { ref, update } from "firebase/database";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import {
  type WizardStateRecord,
  advanceWizard,
  completeWizard,
  skipScenePack,
} from "../lib/wizard-state.js";
import WizardPremiseStep from "./WizardPremiseStep.js";
import WizardRulesetStep from "./WizardRulesetStep.js";
import WizardScenePackStep from "./WizardScenePackStep.js";

interface Props {
  open: boolean;
  tableId: string;
  uid: string | undefined;
  guildId: string | undefined;
  wizardState: WizardStateRecord | undefined;
  currentRulesetId: string | null;
  currentRulesetVersion: string | null;
  installedScenePackIds: string[];
  initialPremiseTitle?: string;
  initialPremiseDesc?: string;
  onClose: () => void;
}

export default function WizardModal(props: Props): ReactElement | null {
  const { t } = useTranslation();
  if (!props.open) return null;
  const step = props.wizardState?.step ?? "ruleset";
  const tablePath = `/tables/${props.tableId}`;

  async function handleAdvance(from: WizardStateRecord["step"]): Promise<void> {
    const { db } = getFirebase();
    await update(ref(db), advanceWizard(tablePath, from));
  }

  async function handleSkipScene(): Promise<void> {
    const { db } = getFirebase();
    await update(ref(db), skipScenePack(tablePath));
  }

  async function handleFinish(): Promise<void> {
    const { db } = getFirebase();
    await update(ref(db), completeWizard(tablePath));
    props.onClose();
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="wizard-title" className="text-lg font-semibold">
            {t("wizard.title")}
          </h2>
          <button
            type="button"
            className="rounded border px-2 py-0.5 text-xs"
            onClick={props.onClose}
          >
            {t("wizard.close")}
          </button>
        </div>
        <div className="text-xs text-muted-foreground">{t(`wizard.step.${step}`)}</div>

        <div className="mt-4">
          {step === "ruleset" && (
            <WizardRulesetStep
              tableId={props.tableId}
              uid={props.uid}
              guildId={props.guildId}
              currentRulesetId={props.currentRulesetId}
              currentRulesetVersion={props.currentRulesetVersion}
              onNext={() => {
                void handleAdvance("ruleset");
              }}
            />
          )}
          {step === "scene" && (
            <WizardScenePackStep
              tableId={props.tableId}
              uid={props.uid}
              guildId={props.guildId}
              installedScenePackIds={props.installedScenePackIds}
              onNext={() => {
                void handleAdvance("scene");
              }}
              onSkip={() => {
                void handleSkipScene();
              }}
            />
          )}
          {step === "premise" && (
            <WizardPremiseStep
              tableId={props.tableId}
              {...(props.initialPremiseTitle !== undefined
                ? { initialTitle: props.initialPremiseTitle }
                : {})}
              {...(props.initialPremiseDesc !== undefined
                ? { initialDesc: props.initialPremiseDesc }
                : {})}
              onDone={handleFinish}
            />
          )}
          {step === "done" && <div className="text-sm">{t("wizard.complete")}</div>}
        </div>
      </div>
    </div>
  );
}
